import { useMemo } from 'react';
import { GAME_MODE, SingSetup, Song } from '~/interfaces';
import RoomStateStore from '~/modules/Multiplayer/RoomState';
import PlayersManager from '~/modules/Players/PlayersManager';
import PostGameView from '~/routes/Game/Singing/PostGame/PostGameView';
import MultiplayerResults from '~/routes/Game/Singing/PostGame/Views/MultiplayerResults';
import useHighScores from '~/routes/Game/Singing/PostGame/hooks/useHighScores';
import SongPage from '~/routes/Game/SongPage';
import LayoutGame from '~/routes/LayoutGame';
import { UnassignOnSongFinishedSetting } from '~/routes/Settings/SettingsState';
import GameState from '../../../../modules/GameEngine/GameState/GameState';

interface Props {
  width: number;
  height: number;
  song: Song;
  onClickSongSelection: () => void;
  singSetup: SingSetup;
}

function MultiplayerPostGame({ song, width, height, onClickSongSelection }: Omit<Props, 'singSetup'>) {
  return (
    <LayoutGame>
      <SongPage songData={song} width={width} height={height}>
        <MultiplayerResults
          localScore={GameState.getPlayerScore(0)}
          onNextSong={onClickSongSelection}
          onBackToLobby={onClickSongSelection}
        />
      </SongPage>
    </LayoutGame>
  );
}

function PostGame({ song, width, height, onClickSongSelection, singSetup }: Props) {
  const isMultiplayer = RoomStateStore.isInRoom || singSetup.mode === GAME_MODE.MULTIPLAYER;

  const highScores = useHighScores(song, singSetup);
  const playerScores = useMemo(
    () =>
      PlayersManager.getPlayers().map((player) => ({
        name: player.getName(),
        playerNumber: player.number,
        detailedScore: GameState.getPlayerDetailedScore(player.number),
      })),
    [],
  );

  if (isMultiplayer) {
    return (
      <MultiplayerPostGame song={song} width={width} height={height} onClickSongSelection={onClickSongSelection} />
    );
  }

  return (
    <LayoutGame>
      <PostGameView
        singSetup={singSetup}
        song={song}
        width={width}
        height={height}
        onClickSongSelection={() => {
          onClickSongSelection();

          if (UnassignOnSongFinishedSetting.get()) {
            PlayersManager.getPlayers().forEach((player) => {
              if (player.input.source === 'Remote Microphone') {
                PlayersManager.removePlayer(player.number);
              }
            });
          }
        }}
        players={playerScores}
        highScores={highScores}
      />
    </LayoutGame>
  );
}

export default PostGame;
